import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase';
import { usePlatformSettings } from '../../context/PlatformSettingsContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
  COMMON_TIME_ZONES,
  formatTimezoneMenuLabel,
  resolveDisplayTimeZone,
} from '../../lib/timezone';

/**
 * Sidebar timezone picker — reads platform settings live; persists platform or
 * company overrides based on the signed-in user's role (no AuthContext / useAuth).
 */
export default function TimezoneSelectorDropdown() {
  const { settings, loading: settingsLoading } = usePlatformSettings();
  const { workspace, companyId: workspaceCompanyId } = useWorkspace();

  const [userRole, setUserRole] = useState(null);
  const [userCompanyId, setUserCompanyId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async (user) => {
      if (!user) {
        if (!cancelled) {
          setUserRole(null);
          setUserCompanyId(null);
          setProfileLoading(false);
        }
        return;
      }
      setProfileLoading(true);
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data();
          setUserRole(typeof data.role === 'string' ? data.role : null);
          setUserCompanyId(typeof data.companyId === 'string' ? data.companyId : null);
        } else {
          setUserRole(null);
          setUserCompanyId(null);
        }
      } catch (err) {
        console.error('TimezoneSelectorDropdown profile:', err);
        if (!cancelled) {
          setUserRole(null);
          setUserCompanyId(null);
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      loadProfile(user);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const effectiveTimeZone = useMemo(
    () => resolveDisplayTimeZone(settings, workspace),
    [settings, workspace],
  );

  const [selectedTimeZone, setSelectedTimeZone] = useState(effectiveTimeZone);

  useEffect(() => {
    if (!settingsLoading) {
      setSelectedTimeZone(effectiveTimeZone);
    }
  }, [effectiveTimeZone, settingsLoading]);

  const resolvedCompanyId = workspaceCompanyId || userCompanyId;

  const canWrite = useMemo(() => {
    if (userRole === 'super_admin') return true;
    const delegates = settings?.timezoneDelegateRoles || [];
    return Boolean(userRole && resolvedCompanyId && delegates.includes(userRole));
  }, [userRole, resolvedCompanyId, settings?.timezoneDelegateRoles]);

  const persistTimeZone = useCallback(
    async (timeZone) => {
      setSaving(true);
      setError('');
      try {
        if (userRole === 'super_admin') {
          const actor = auth.currentUser;
          await setDoc(
            doc(db, 'platformSettings', 'global'),
            {
              displayTimeZone: timeZone,
              updatedAt: serverTimestamp(),
              updatedBy: actor?.uid || null,
            },
            { merge: true },
          );
          return;
        }

        if (!resolvedCompanyId) {
          throw new Error('No workspace linked to update timezone.');
        }

        const fn = httpsCallable(functions, 'updateCompanyDisplayTimeZone');
        await fn({
          companyId: resolvedCompanyId,
          displayTimeZone: timeZone,
        });
      } catch (err) {
        console.error('TimezoneSelectorDropdown save:', err);
        setError(err.message || 'Could not save timezone.');
        setSelectedTimeZone(effectiveTimeZone);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [userRole, resolvedCompanyId, effectiveTimeZone],
  );

  const handleChange = async (event) => {
    const next = event.target.value;
    if (!canWrite || next === selectedTimeZone) return;
    setSelectedTimeZone(next);
    try {
      await persistTimeZone(next);
    } catch {
      // error state already handled in persistTimeZone
    }
  };

  const busy = settingsLoading || profileLoading || saving;

  return (
    <>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, px: 0.5 }}
      >
        <ScheduleIcon sx={{ fontSize: 14 }} />
        Display timezone
      </Typography>
      <FormControl fullWidth size="small" disabled={busy || !canWrite}>
        <InputLabel id="sidebar-timezone-label">Timezone</InputLabel>
        <Select
          labelId="sidebar-timezone-label"
          label="Timezone"
          value={selectedTimeZone}
          onChange={handleChange}
          renderValue={(value) => {
            const zone = COMMON_TIME_ZONES.find((z) => z.id === value);
            return zone ? formatTimezoneMenuLabel(zone) : value;
          }}
        >
          {COMMON_TIME_ZONES.map((zone) => (
            <MenuItem key={zone.id} value={zone.id}>
              {formatTimezoneMenuLabel(zone)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', px: 0.5 }}>
          {error}
        </Typography>
      )}
      {!canWrite && !busy && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', px: 0.5 }}>
          View only — contact an admin to change.
        </Typography>
      )}
    </>
  );
}
